import { type FormikErrors } from 'formik';
import React from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import {
  type FormValues,
  TopUpModalLayout,
} from 'src/core/modules/cards/components/TopUpModalLayout';
import { formatMoney } from 'src/core/utils/money';

import { type ApiCard } from '../../card';
import { useRequestCardTopupMutation } from '../../hooks/useRequestCardTopupMutation';
import { useTopupCardMutation } from '../../hooks/useTopupCardMutation';

type Props = {
  card: ApiCard;
  mode: 'admin' | 'requester';
  onProcessed: (card?: ApiCard) => void;
  onCancel: () => void;
};

export const TopUpModal = ({ card, mode, onProcessed, onCancel }: Props) => {
  const { t } = useTranslation('global');
  const company = useCompany();

  const [topupCard] = useTopupCardMutation(card.id);
  const [requestCardTopup] = useRequestCardTopupMutation();

  const validate = async (
    values: FormValues,
  ): Promise<FormikErrors<FormValues>> => {
    const errors: FormikErrors<FormValues> = {};

    if (
      !values.amount ||
      Number.isNaN(values.amount) ||
      values.amount < 0 ||
      !/^\d+(\.|,)?\d*$/.test(String(values.amount))
    ) {
      errors.amount = t('cards.actions.errorAmountFormatManager');
    }

    const maxLoad = card.ceilings.maxLoadsAmount;
    const maxLoadYear = card.ceilings.maxLoadsAmountPerYear;

    if (values.amount && values.amount > maxLoad) {
      errors.amount = t('cards.actions.warningTopUpTooHighWithAmount', {
        amount: formatMoney(maxLoad, card.currency),
      });
    }

    if (values.amount && card.load_year + values.amount > maxLoadYear) {
      errors.amount = t('cards.actions.warningYearlyBudgetTooHighWithAmount', {
        amount: formatMoney(maxLoadYear, card.currency),
      });
    }

    if (mode === 'requester' && !values.description.trim()) {
      errors.description = t('misc.requiredField');
    }

    return errors;
  };

  const handleAdminSubmit = async (amount: number): Promise<void> => {
    // Admin mode, only the amount is required
    const updatedCard = await topupCard({ amount });
    onProcessed(updatedCard);
  };

  const handleRequesterSubmit = async (
    amount: number,
    description: string,
  ): Promise<void> => {
    await requestCardTopup({
      type: 'card_load',
      currency_declared: company.currency,
      amount_declared: amount,
      description,
      user_message: '',
      custom_fields_associations: [],
      get_card_allowed: false,
      is_approved: false,
      is_single_purchase: false,
      is_subscription: false,
      is_card_load: true,
    });
    onProcessed();
  };

  const title =
    mode === 'admin'
      ? t('cards.actions.topUpModalTitleWithName', {
          name: card.user.first_name,
        })
      : t('cards.actions.topUpModalTitle');

  return (
    <TopUpModalLayout
      mode={mode}
      title={title}
      onCancel={onCancel}
      onSubmitAdmin={handleAdminSubmit}
      onSubmitRequester={handleRequesterSubmit}
      validate={validate}
    />
  );
};
